import React from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, type AnalyticsEvent } from '../../utils/analytics';
import { cn } from '../../src/lib/utils';
type CtaButtonProps = {
  href: string;
  children: React.ReactNode;
  /** `violet` / `outlineDark` are for dark sections. */
  variant?: 'primary' | 'outline' | 'ghost' | 'violet' | 'outlineDark';
  size?: 'md' | 'lg' | 'xl';
  className?: string;
  ariaLabel?: string;
  /** Analytics event fired on click */
  event?: AnalyticsEvent;
  /** Extra non-personal analytics parameters (e.g. placement, tool slug). */
  eventParams?: Record<string, string>;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};
const VARIANTS: Record<NonNullable<CtaButtonProps['variant']>, string> = {
  primary: 'bg-lime text-ink shadow-sm hover:bg-[#2B1E7A] hover:shadow-md active:bg-lime-dim',
  outline: 'border border-hairline bg-panel text-text-primary hover:border-[#D8D4EA] hover:bg-[#FCFCFE] active:bg-elevated',
  ghost: 'text-text-secondary hover:text-text-primary',
  violet: 'bg-violet text-white shadow-[0_8px_24px_-12px_rgba(124,92,255,0.7)] hover:bg-violet-strong active:bg-violet-strong',
  outlineDark: 'border border-night-line bg-white/[0.03] text-night-text hover:border-violet/60 hover:bg-white/[0.06]'
};
const SIZES = {
  md: 'px-5 py-2.5 text-[13px]',
  lg: 'px-6 py-3.5 text-sm',
  xl: 'px-8 py-4 text-[15px]'
};

/**
 * Button-styled link. Internal paths ("/…") use client-side routing; "#…" anchors
 * stay in-page; everything else opens in a new tab.
 */
export function CtaButton({
  href,
  children,
  variant = 'primary',
  size = 'md',
  className,
  ariaLabel,
  event,
  eventParams,
  onClick
}: CtaButtonProps) {
  const classes = cn('inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg font-semibold tracking-[-0.005em]', 'transition-[background-color,color,border-color,box-shadow] duration-200 ease-out', variant === 'violet' || variant === 'outlineDark' ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet focus-visible:ring-offset-2 focus-visible:ring-offset-night' : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-ink', SIZES[size], VARIANTS[variant], className);
  const handleClick = (clickEvent: React.MouseEvent<HTMLAnchorElement>) => {
    if (event) trackEvent(event, {
      destination: href,
      ...eventParams
    });
    onClick?.(clickEvent);
  };
  if (href.startsWith('/')) {
    return <Link to={href} aria-label={ariaLabel} onClick={handleClick} className={classes} data-id="element-1647" data-mp="2w5zrtj8z7tzc">
        {children}
      </Link>;
  }
  const isAnchor = href.startsWith('#');
  return <a href={href} aria-label={ariaLabel} onClick={handleClick} {...isAnchor ? {} : {
    target: '_blank',
    rel: 'noopener noreferrer'
  }} className={classes} data-id="element-1648" data-mp="3577dn1ub32cj">
      {children}
    </a>;
}